/**
 * useOptimisticCRUD - Generic hook for optimistic CRUD operations
 * 
 * Standard SaaS pattern for instant UI feedback with background sync.
 * 
 * Usage:
 * ```typescript
 * const { create, update, remove } = useOptimisticCRUD({
 *   // Optimistic UI callbacks (update local state immediately)
 *   onOptimisticAdd: (item) => addToList(item),
 *   onOptimisticUpdate: (item) => updateInList(item),
 *   onOptimisticRemove: (id) => removeFromList(id),
 *   onOptimisticReplace: (tempId, item) => replaceInList(tempId, item),
 *   
 *   // API service methods
 *   service: {
 *     create: (data) => api.create(data),
 *     update: (id, data) => api.update(id, data),
 *     delete: (id) => api.delete(id),
 *   },
 *   
 *   // Rollback on error
 *   onRollback: () => silentRefresh(),
 * });
 * ```
 */

import { useRef } from 'react';
import { blockDemoWrite } from '../services/dataScope';

export interface OptimisticCRUDService<T, CreateInput, TQuotaInfo = unknown> {
  create: (data: CreateInput) => Promise<T | OptimisticQuotaBlocked<TQuotaInfo> | null>;
  update: (id: string, data: Partial<T>) => Promise<T | null>;
  delete: (id: string) => Promise<boolean>;
}

/** Returned by service.create when quota blocks creation — keeps optimistic UI until resolved. */
export type OptimisticQuotaBlocked<TQuotaInfo = unknown> = {
  __quotaBlocked: true;
  quotaInfo: TQuotaInfo;
};

export function isOptimisticQuotaBlocked(
  result: unknown
): result is OptimisticQuotaBlocked {
  return (
    !!result &&
    typeof result === 'object' &&
    (result as OptimisticQuotaBlocked).__quotaBlocked === true
  );
}

export interface UseOptimisticCRUDOptions<T, CreateInput, TQuotaInfo = unknown> {
  /** Called immediately to add item to UI (before API) */
  onOptimisticAdd: (item: T) => void;
  /** Called immediately to update item in UI (before API) */
  onOptimisticUpdate: (item: T) => void;
  /** Called immediately to remove item from UI (before API) */
  onOptimisticRemove: (id: string) => void;
  /** Called to replace temp item with real one from API */
  onOptimisticReplace: (tempId: string, realItem: T) => void;
  /** API service for actual persistence */
  service: OptimisticCRUDService<T, CreateInput, TQuotaInfo>;
  /** Called on error to rollback optimistic changes */
  onRollback: () => void;
  /** Optional: called when an update fails before rollback (e.g. show toast) */
  onUpdateFailed?: () => void;
  /** Optional: Generate temp ID (default: `temp-${Date.now()}`) */
  generateTempId?: () => string;
  /**
   * When create returns a quota-blocked sentinel, keep the optimistic item if this returns true.
   * Caller should wire dismiss / retry (e.g. archive then recreate).
   */
  onQuotaBlocked?: (ctx: {
    tempId: string;
    data: CreateInput;
    quotaInfo: TQuotaInfo;
  }) => boolean;
}

export interface UpdateOptions<T> {
  /** If provided and update fails, revert this item in UI (local rollback) instead of refetch. */
  previousItem?: T;
}

export interface UseOptimisticCRUDReturn<T, CreateInput> {
  /** Create item with optimistic UI */
  create: (data: CreateInput, optimisticItem: T) => void;
  /** Update item with optimistic UI. Pass previousItem for local rollback on failure (no refetch). */
  update: (id: string, data: T, options?: UpdateOptions<T>) => void;
  /** Remove item with optimistic UI */
  remove: (id: string) => void;
  /** Bulk remove items with optimistic UI */
  bulkRemove: (ids: string[]) => void;
  /** Bulk update items with optimistic UI */
  bulkUpdate: (items: T[]) => void;
}

export const useOptimisticCRUD = <T extends { id: string }, CreateInput, TQuotaInfo = unknown>(
  options: UseOptimisticCRUDOptions<T, CreateInput, TQuotaInfo>
): UseOptimisticCRUDReturn<T, CreateInput> => {
  const {
    onOptimisticAdd,
    onOptimisticUpdate,
    onOptimisticRemove,
    onOptimisticReplace,
    service,
    onRollback,
    onUpdateFailed,
    onQuotaBlocked,
  } = options;

  /**
   * SaaS-grade reconciliation for optimistic creates:
   * - User can UPDATE a temp item before the server CREATE returns: we queue the latest patch and apply it to the real item.
   * - User can DELETE a temp item before the server CREATE returns: we treat it as "cancel create" (no delete(tempId) call),
   *   and once the server returns the real id we delete that record in background.
   */
  const pendingUpdateByTempIdRef = useRef<Map<string, Partial<T>>>(new Map());
  const cancelledCreateTempIdsRef = useRef<Set<string>>(new Set());
  const tempToRealIdRef = useRef<Map<string, string>>(new Map());
  /** For local rollback: when update fails, revert to this item instead of refetch (if provided). */
  const previousItemByUpdateIdRef = useRef<Map<string, T>>(new Map());

  const isTempId = (id: string): boolean => id.startsWith('temp-');

  const stripId = (item: T): Partial<T> => {
    const { id: _id, ...rest } = item as any;
    return rest as Partial<T>;
  };

  /**
   * Create with optimistic UI
   * 1. Add to UI immediately with temp data
   * 2. Call API in background
   * 3. Replace temp with real data on success, rollback on error
   */
  const create = (data: CreateInput, optimisticItem: T): void => {
    // Demo workspace is read-only — don't optimistic-add then fail with an error toast.
    if (blockDemoWrite()) return;

    const tempId = optimisticItem.id;
    
    // Reset any previous tracking for this temp id (defensive)
    pendingUpdateByTempIdRef.current.delete(tempId);
    cancelledCreateTempIdsRef.current.delete(tempId);
    tempToRealIdRef.current.delete(tempId);

    // 1. Optimistic: add to UI immediately
    onOptimisticAdd(optimisticItem);
    
    // 2. API call in background
    service.create(data)
      .then((raw) => {
        if (isOptimisticQuotaBlocked(raw)) {
          const keep = onQuotaBlocked?.({
            tempId,
            data,
            quotaInfo: raw.quotaInfo as TQuotaInfo,
          }) ?? false;
          if (!keep) {
            onOptimisticRemove(tempId);
            pendingUpdateByTempIdRef.current.delete(tempId);
            cancelledCreateTempIdsRef.current.delete(tempId);
            tempToRealIdRef.current.delete(tempId);
          }
          return;
        }

        const created = raw as T | null;
        if (created) {
          // Server created the real record
          tempToRealIdRef.current.set(tempId, created.id);

          // If user deleted the temp item before create resolved, we must not re-add it.
          if (cancelledCreateTempIdsRef.current.has(tempId)) {
            // Ensure temp is not visible (might already be gone)
            onOptimisticRemove(tempId);

            // Best-effort delete of the real record (created server-side)
            service.delete(created.id)
              .then((success) => {
                if (!success) onRollback();
              })
              .catch((error: unknown) => {
                console.error('Error deleting newly created (but cancelled) item:', error);
                onRollback();
              })
              .finally(() => {
                cancelledCreateTempIdsRef.current.delete(tempId);
                pendingUpdateByTempIdRef.current.delete(tempId);
                // Cancelled create: drop mapping (no future updates should resolve).
                tempToRealIdRef.current.delete(tempId);
              });

            return;
          }

          // If user updated the temp item before create resolved, apply the latest patch to the real item.
          const pendingPatch = pendingUpdateByTempIdRef.current.get(tempId);
          const merged = pendingPatch
            ? ({ ...created, ...pendingPatch, id: created.id } as T)
            : created;

          // 3a. Success: replace temp with real item (merged with local changes if needed)
          onOptimisticReplace(tempId, merged);

          // Persist pending patch (if any) on the real id, best-effort.
          if (pendingPatch && Object.keys(pendingPatch).length > 0) {
            service.update(created.id, pendingPatch)
              .catch((error: unknown) => {
                console.error('Error persisting queued update after create:', error);
                onRollback();
              })
              .finally(() => {
                pendingUpdateByTempIdRef.current.delete(tempId);
                // IMPORTANT: keep temp→real mapping for a while.
                // Some UI flows may still hold a stale temp id right after create replaces the item.
              });
          } else {
            pendingUpdateByTempIdRef.current.delete(tempId);
            // IMPORTANT: keep temp→real mapping for a while.
            // Some UI flows may still hold a stale temp id right after create replaces the item.
          }
        } else {
          // 3b. API returned null
          onOptimisticRemove(tempId);
          pendingUpdateByTempIdRef.current.delete(tempId);
          cancelledCreateTempIdsRef.current.delete(tempId);
          tempToRealIdRef.current.delete(tempId);
        }
      })
      .catch((error: unknown) => {
        console.error('Error creating item:', error);
        // 3c. Error: remove temp item
        onOptimisticRemove(tempId);
        pendingUpdateByTempIdRef.current.delete(tempId);
        cancelledCreateTempIdsRef.current.delete(tempId);
        tempToRealIdRef.current.delete(tempId);
      });
  };

  /**
   * Update with optimistic UI
   * 1. Update UI immediately
   * 2. Call API in background
   * 3. On error: if previousItem provided, revert that item locally (no refetch); else onRollback (refetch)
   */
  const update = (id: string, data: T, options?: UpdateOptions<T>): void => {
    if (blockDemoWrite()) return;

    const previousItem = options?.previousItem;
    if (previousItem != null) {
      previousItemByUpdateIdRef.current.set(id, previousItem);
    }

    // 1. Optimistic: update UI immediately
    onOptimisticUpdate(data);

    // If it's still a temp item, we may race with create() reconciliation:
    // - create resolved and replaced tempId in the list, but the UI still holds a stale tempId reference.
    // In that case, resolve to the real id and update that record instead of queuing a patch that will never flush.
    if (isTempId(id)) {
      const realId = tempToRealIdRef.current.get(id);
      if (realId) {
        // Update the real record in UI + persist in background.
        onOptimisticUpdate({ ...(data as any), id: realId } as T);
        service.update(realId, stripId({ ...(data as any), id: realId } as T))
          .then((result) => {
            if (result === null) {
              onUpdateFailed?.();
              onRollback();
            }
          })
          .catch((error: unknown) => {
            console.error('Error updating item (resolved temp id):', error);
            onUpdateFailed?.();
            onRollback();
          });
        return;
      }
      pendingUpdateByTempIdRef.current.set(id, stripId(data));
      return;
    }

    // 2. API call in background; on failure: local rollback if previousItem, else refetch
    service.update(id, data)
      .then((result) => {
        if (result === null) {
          console.error('Update returned null (API/DB error)');
          const prev = previousItemByUpdateIdRef.current.get(id);
          previousItemByUpdateIdRef.current.delete(id);
          if (prev != null) {
            onOptimisticUpdate(prev);
            onUpdateFailed?.();
          } else {
            onUpdateFailed?.();
            onRollback();
          }
        }
      })
      .catch((error: unknown) => {
        console.error('Error updating item:', error);
        const prev = previousItemByUpdateIdRef.current.get(id);
        previousItemByUpdateIdRef.current.delete(id);
        if (prev != null) {
          onOptimisticUpdate(prev);
          onUpdateFailed?.();
        } else {
          onUpdateFailed?.();
          onRollback();
        }
      });
  };

  /**
   * Remove with optimistic UI
   * 1. Remove from UI immediately
   * 2. Call API in background
   * 3. Rollback on error
   */
  const remove = (id: string): void => {
    if (blockDemoWrite()) return;

    // 1. Optimistic: remove from UI immediately
    onOptimisticRemove(id);

    // If it's a temp id, treat it as "cancel create" and don't call delete(tempId).
    // The server record may still be created; create() reconciliation will delete it using the real id.
    if (isTempId(id)) {
      cancelledCreateTempIdsRef.current.add(id);
      pendingUpdateByTempIdRef.current.delete(id);
      return;
    }

    // 2. API call in background
    service.delete(id)
      .then((success) => {
        if (!success) {
          // 3a. API returned false
          onRollback();
        }
      })
      .catch((error: unknown) => {
        console.error('Error deleting item:', error);
        // 3b. Error: rollback
        onRollback();
      });
  };

  /**
   * Bulk remove with optimistic UI
   */
  const bulkRemove = (ids: string[]): void => {
    if (blockDemoWrite()) return;

    const realIds: string[] = [];

    // 1. Optimistic: remove all from UI immediately
    ids.forEach((id) => {
      onOptimisticRemove(id);
      if (isTempId(id)) {
        cancelledCreateTempIdsRef.current.add(id);
        pendingUpdateByTempIdRef.current.delete(id);
      } else {
        realIds.push(id);
      }
    });

    // 2. API calls in parallel (real ids only)
    if (realIds.length === 0) return;

    Promise.all(realIds.map((id) => service.delete(id)))
      .then((results) => {
        // Validation: if any delete returns false, rollback
        if (results.some((ok) => !ok)) {
          onRollback();
        }
      })
      .catch((error: unknown) => {
        console.error('Error bulk deleting items:', error);
        onRollback();
      });
  };

  /**
   * Bulk update with optimistic UI
   */
  const bulkUpdate = (items: T[]): void => {
    if (blockDemoWrite()) return;

    const updates: Array<Promise<T | null>> = [];

    // 1. Optimistic: update all in UI immediately
    items.forEach((item) => {
      onOptimisticUpdate(item);

      if (isTempId(item.id)) {
        pendingUpdateByTempIdRef.current.set(item.id, stripId(item));
        return;
      }

      updates.push(service.update(item.id, item));
    });

    // 2. API calls in parallel (real ids only)
    if (updates.length === 0) return;

    Promise.all(updates)
      .then((results) => {
        // Validation: if any update returns null, treat as failure and rollback
        if (results.some((r) => r === null)) {
          onUpdateFailed?.();
          onRollback();
        }
      })
      .catch((error: unknown) => {
        console.error('Error bulk updating items:', error);
        onUpdateFailed?.();
        onRollback();
      });
  };

  return {
    create,
    update,
    remove,
    bulkRemove,
    bulkUpdate,
  };
};

/**
 * Helper: Generate a temporary ID for optimistic creates
 */
export const generateTempId = (): string => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Helper: Create an optimistic item with temp ID and timestamps
 */
export const createOptimisticItem = <T extends { id: string; createdAt?: Date; updatedAt?: Date }>(
  data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>
): T => {
  const now = new Date();
  return {
    id: generateTempId(),
    ...data,
    createdAt: now,
    updatedAt: now,
  } as T;
};
